/**
 * Analytics plugin pro automatické sledování návštěvníků
 */
import { isAffiliateEmbedPath } from '~/constants/affiliate'
import { ADMIN_DEVICE_KEY, LINK_PAGE_PREFIX } from '~/constants/analytics'

export default defineNuxtPlugin(async () => {
  // Spouští se pouze na klientovi
  if (!process.client) return
  // A partner's widget shown on their site is not a visit to ours.
  if (isAffiliateEmbedPath(window.location.pathname)) return

  const { trackEvent, trackPageView } = useGTM()
  const router = useRouter()
  const route = useRoute()

  // Generování unikátního ID session
  const generateSessionId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2)
  }

  // Získání nebo vytvoření visitor ID
  const getOrCreateVisitorId = (): string => {
    const stored = localStorage.getItem('visitor_id')
    if (stored) return stored

    const visitorId = generateSessionId()
    localStorage.setItem('visitor_id', visitorId)
    return visitorId
  }

  // Kontrola tracking preference
  const isTrackingEnabled = (): boolean => {
    if (localStorage.getItem(ADMIN_DEVICE_KEY) === 'true') return false
    const stored = localStorage.getItem('analytics_tracking_enabled')
    return stored !== 'false' // default true, unless explicitly disabled
  }

  // The server refuses to record a signed-in admin; remember the device so it
  // stays out of the stats after the admin signs out too.
  const rememberIfAdmin = (response: any) => {
    if (response?.reason === 'admin') localStorage.setItem(ADMIN_DEVICE_KEY, 'true')
  }

  // Detekce zařízení a prohlížeče
  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent
    let deviceType = 'desktop'

    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      deviceType = 'mobile'
    } else if (/iPad/i.test(userAgent)) {
      deviceType = 'tablet'
    }

    // Detekce prohlížeče
    let browser = 'unknown'
    if (userAgent.includes('Chrome')) browser = 'chrome'
    else if (userAgent.includes('Firefox')) browser = 'firefox'
    else if (userAgent.includes('Safari')) browser = 'safari'
    else if (userAgent.includes('Edge')) browser = 'edge'

    return {
      deviceType,
      browser,
      userAgent,
      screenResolution: `${screen.width}x${screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  }

  // Získání geolokace (IP-based)
  const getLocationData = async () => {
    try {
      const response = await $fetch('https://ipapi.co/json/') as any
      return {
        country: response.country_name,
        city: response.city,
        region: response.region,
        ip: response.ip
      }
    } catch (error) {
      return null
    }
  }

  // Extrakce search terms z Google referrer nebo URL parametrů
  const extractSearchTerms = (): string | undefined => {
    // 1. Zkusíme utm_term z URL parametrů (nejvyšší priorita)
    if (route.query.utm_term) {
      return route.query.utm_term as string
    }

    // 2. Zkusíme 'q' parametr z URL (pro přímé odkazy z Google)
    if (route.query.q) {
      return route.query.q as string
    }

    // 3. Zkusíme extrahovat z referrer URL
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer)
        const hostname = referrerUrl.hostname.toLowerCase()

        // Google search
        if (hostname.includes('google.')) {
          const searchQuery = referrerUrl.searchParams.get('q')
          if (searchQuery) {
            return searchQuery
          }
        }

        // Bing search
        if (hostname.includes('bing.')) {
          const searchQuery = referrerUrl.searchParams.get('q')
          if (searchQuery) {
            return searchQuery
          }
        }

        // Seznam.cz search
        if (hostname.includes('seznam.cz')) {
          const searchQuery = referrerUrl.searchParams.get('q') || referrerUrl.searchParams.get('w')
          if (searchQuery) {
            return searchQuery
          }
        }

        // DuckDuckGo search
        if (hostname.includes('duckduckgo.')) {
          const searchQuery = referrerUrl.searchParams.get('q')
          if (searchQuery) {
            return searchQuery
          }
        }

        // Yahoo search
        if (hostname.includes('yahoo.')) {
          const searchQuery = referrerUrl.searchParams.get('p')
          if (searchQuery) {
            return searchQuery
          }
        }
      } catch (error) {
        console.warn('Failed to parse referrer URL for search terms:', error)
      }
    }

    return undefined
  }

  // The campaign of the visit. Google Ads' auto-tagging brings only gclid
  // (gbraid/wbraid from iOS) and gad_campaignid, so those stand in for the
  // source and campaign; the keyword comes only from the account's tracking
  // template or final URL suffix (utm_term={keyword}, or keyword={keyword}).
  const campaignOf = (searchTerms?: string) => {
    const param = (key: string) => {
      const value = route.query[key]
      return (Array.isArray(value) ? value[0] : value) || undefined
    }
    const adClick = !!(param('gclid') || param('gbraid') || param('wbraid') || param('gad_source'))
    return {
      utm_source: param('utm_source') || (adClick ? 'google' : undefined),
      utm_medium: param('utm_medium') || (adClick ? 'cpc' : undefined),
      utm_campaign: param('utm_campaign') || param('gad_campaignid') || param('campaignid'),
      utm_content: param('utm_content'),
      utm_term: param('utm_term') || param('keyword') || searchTerms
    }
  }

  // Inicializace session
  const initializeSession = async () => {
    if (!isTrackingEnabled()) return

    // A reload or a link opened in the same tab continues the session already
    // recorded instead of starting a new one.
    if (sessionStorage.getItem('session_id')) return

    const visitorId = getOrCreateVisitorId()
    const sessionId = generateSessionId()

    // Stored before the IP lookup below: the landing page view is sent 100 ms
    // after start and was dropped for want of a session id while it ran.
    sessionStorage.setItem('session_id', sessionId)
    sessionStorage.setItem('session_start', new Date().toISOString())

    const deviceInfo = getDeviceInfo()
    const locationData = await getLocationData()
    const searchTerms = extractSearchTerms()

    // Odeslání dat o nové session
    const sessionData = {
      visitor_id: visitorId,
      session_id: sessionId,
      started_at: new Date().toISOString(),
      device_info: deviceInfo,
      location_data: locationData,
      referrer: document.referrer,
      landing_page: route.path,
      ...campaignOf(searchTerms)
    }

    // GTM tracking
    trackEvent('session_start', sessionData)

    // API call pro uložení do databáze
    try {
      rememberIfAdmin(await $fetch('/api/analytics/session', {
        method: 'POST',
        body: sessionData
      }))
    } catch (error) {
      console.warn('Failed to track session:', error)
    }
  }

  // Sledování page views
  const trackPageViewAnalytics = (to: any) => {
    if (!isTrackingEnabled()) return

    const visitorId = getOrCreateVisitorId()
    const sessionId = sessionStorage.getItem('session_id')

    // Kontrola, zda máme session ID
    if (!sessionId) {
      console.warn('No session ID found, skipping page view tracking')
      return
    }

    const pageViewData = {
      visitor_id: visitorId,
      session_id: sessionId,
      page_path: to.path,
      page_title: document.title,
      page_url: window.location.href,
      timestamp: new Date().toISOString(),
      referrer: document.referrer
    }

    // GTM tracking
    trackPageView(document.title, to.path)
    trackEvent('page_view', pageViewData)

    // API call
    $fetch('/api/analytics/pageview', {
      method: 'POST',
      body: pageViewData
    }).then(rememberIfAdmin).catch(error => {
      console.warn('Failed to track page view:', error)
    })
  }

  // Sledování kliknutí na odkazy
  const trackLinkClicks = () => {
    if (!isTrackingEnabled()) return

    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const link = target.closest('a')

      if (link && link.href) {
        const visitorId = getOrCreateVisitorId()
        const sessionId = sessionStorage.getItem('session_id')

        // Kontrola, zda máme session ID
        if (!sessionId) {
          console.warn('No session ID found, skipping link click tracking')
          return
        }

        const clickData = {
          visitor_id: visitorId,
          session_id: sessionId,
          link_url: link.href,
          link_text: link.textContent?.trim(),
          page_path: route.path,
          timestamp: new Date().toISOString(),
          is_external: !link.href.includes(window.location.hostname)
        }

        trackEvent('link_click', clickData)

        $fetch('/api/analytics/event', {
          method: 'POST',
          body: {
            event_type: 'link_click',
            ...clickData
          }
        }).catch(error => {
          console.warn('Failed to track link click:', error)
        })
      }
    })
  }

  // Sledování scroll depth
  const trackScrollDepth = () => {
    if (!isTrackingEnabled()) return

    let maxScrollDepth = 0
    let scrollTimer: NodeJS.Timeout

    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimer)

      scrollTimer = setTimeout(() => {
        const scrollTop = window.pageYOffset
        const windowHeight = window.innerHeight
        const documentHeight = document.documentElement.scrollHeight
        const scrollPercentage = Math.round((scrollTop + windowHeight) / documentHeight * 100)

        if (scrollPercentage > maxScrollDepth) {
          // Sledujeme milestone body (25%, 50%, 75%, 100%): the deepest one
          // passed by this scroll. Compared before maxScrollDepth moves on; it
          // was compared after, so no milestone was ever sent.
          const milestone = [100, 75, 50, 25].find(m => scrollPercentage >= m && maxScrollDepth < m)
          maxScrollDepth = scrollPercentage

          if (milestone) {
            const visitorId = getOrCreateVisitorId()
            const sessionId = sessionStorage.getItem('session_id')

            // Kontrola, zda máme session ID
            if (!sessionId) {
              console.warn('No session ID found, skipping scroll tracking')
              return
            }

            const scrollData = {
              visitor_id: visitorId,
              session_id: sessionId,
              page_path: route.path,
              scroll_depth: milestone,
              timestamp: new Date().toISOString()
            }

            trackEvent('scroll_depth', scrollData)

            $fetch('/api/analytics/event', {
              method: 'POST',
              body: {
                event_type: 'scroll_depth',
                ...scrollData
              }
            }).catch(error => {
              console.warn('Failed to track scroll depth:', error)
            })
          }
        }
      }, 250)
    })

    // Each page is scrolled from the top again.
    router.afterEach(() => {
      maxScrollDepth = 0
    })
  }

  // Sledování času na stránce
  const trackTimeOnPage = () => {
    if (!isTrackingEnabled()) return

    let pageStartTime = Date.now()

    const sendTimeOnPage = () => {
      const timeSpent = Math.round((Date.now() - pageStartTime) / 1000)

              if (timeSpent > 10) { // Pouze pokud strávil více než 10 sekund
          const visitorId = getOrCreateVisitorId()
          const sessionId = sessionStorage.getItem('session_id')

          // Kontrola, zda máme session ID
          if (!sessionId) {
            console.warn('No session ID found, skipping time tracking')
            return
          }

          const timeData = {
            visitor_id: visitorId,
            session_id: sessionId,
            page_path: route.path,
            time_spent: timeSpent,
            timestamp: new Date().toISOString()
          }

        trackEvent('time_on_page', timeData)

        $fetch('/api/analytics/event', {
          method: 'POST',
          body: {
            event_type: 'time_on_page',
            ...timeData
          }
        }).catch(error => {
          console.warn('Failed to track time on page:', error)
        })
      }
    }

    // Odeslání při opuštění stránky
    window.addEventListener('beforeunload', sendTimeOnPage)

    // Reset při změně route
    router.afterEach(() => {
      sendTimeOnPage()
      pageStartTime = Date.now()
    })
  }

  // Počkáme na DOM ready
  const initAnalytics = () => {
    // A recruiter on a link for one company is not tracked as our visitor.
    if (window.location.pathname.startsWith(LINK_PAGE_PREFIX)) return

    // Inicializace
    initializeSession()

    // Started after hydration, so the first navigation has already happened and
    // the afterEach below would miss it; count the landing page here instead.
    setTimeout(() => {
      trackPageViewAnalytics(router.currentRoute.value)
    }, 100)

    // Sledování route změn
    router.afterEach((to) => {
      // Malé zpoždění pro zajištění, že document.title je aktualizován
      setTimeout(() => {
        trackPageViewAnalytics(to)
      }, 100)
    })

    // Spuštění ostatních tracking funkcí
    setTimeout(() => {
      trackLinkClicks()
      trackScrollDepth()
      trackTimeOnPage()
    }, 1000)

    // Sledování ukončení session
    window.addEventListener('beforeunload', () => {
      if (!isTrackingEnabled()) return

      const visitorId = getOrCreateVisitorId()
      const sessionId = sessionStorage.getItem('session_id')
      const sessionStart = sessionStorage.getItem('session_start')

      if (sessionStart) {
        const sessionDuration = Math.round((Date.now() - new Date(sessionStart).getTime()) / 1000)

        const endData = {
          visitor_id: visitorId,
          session_id: sessionId,
          ended_at: new Date().toISOString(),
          duration: sessionDuration
        }

        trackEvent('session_end', endData)

        // Použití sendBeacon pro spolehlivé odeslání
        if (navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify(endData)], { type: 'application/json' })
          navigator.sendBeacon('/api/analytics/session-end', blob)
        } else {
          // Fallback pro prohlížeče bez sendBeacon
          $fetch('/api/analytics/session-end', {
            method: 'POST',
            body: endData
          }).catch(error => {
            console.warn('Failed to track session end:', error)
          })
        }
      }
    })
  }

  // After hydration, once the browser is idle: the session setup (device
  // detection, the IP lookup, the first API calls) used to run before the page
  // hydrated, on the same main thread that makes the page interactive.
  onNuxtReady(initAnalytics)
})
